import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Crown, Gamepad2, Mic, Lock, Scale, RefreshCw, Mail } from "lucide-react";
import { useContact } from "@/components/useContent";

const SECTIONS = [
  {
    id: "membership",
    icon: Crown,
    title: "1. Membership, Trial & Billing",
    body: [
      "Medusa Room VIP membership is offered at $9.99 USD per month or $99 USD per year. New monthly memberships begin with a 7-day free trial: you are not charged until the trial ends, and we email a reminder two days before the first charge.",
      "Memberships renew automatically until cancelled. You can cancel at any time from your account or through the Stripe customer portal link in your receipt; access continues until the end of the paid period. Because vault content is digital and instantly accessible, payments are otherwise final except where law requires otherwise.",
      "One membership per person. Accounts that share vault access or redistribute member content will be terminated without refund.",
    ],
  },
  {
    id: "vault",
    icon: Lock,
    title: "2. VIP Vault Content Rules",
    body: [
      "Vault drops — links, files, notes, and any material marked members-only — are for your personal, non-commercial use. Do not repost, resell, leak, or redistribute vault content or your access.",
      "Vault availability rotates. Content may be added or removed at the label's discretion; membership does not guarantee any specific item remains available.",
    ],
  },
  {
    id: "arcade",
    icon: Gamepad2,
    title: "3. Arcade Chamber: Games, Leaderboards & Prizes",
    body: [
      "The five arcade games — Piano Scales, Venom Drop, Serpent Spiral, Drop the Bass, and Beat Builder — are free to play. Each game keeps its own public leaderboard showing the player name you submit.",
      "Prize tiers are posted beside each game. Prizes are awarded once per qualifying run and must be claimed with a valid email address at score submission. Discount prizes are delivered as unique single-use codes redeemable at beat checkout; they are non-transferable and carry no cash value. Free leases and sample kits are fulfilled by the label over email, typically within 7 days.",
      "Automated play, score tampering, or abuse results in score removal, forfeiture of prizes, and a permanent ban from the room. The label may adjust prize tiers or retire prizes at any time; unclaimed prizes from completed runs are honored.",
    ],
  },
  {
    id: "snakepit",
    icon: Mic,
    title: "4. Snake Pit Submissions & Ghost Verses",
    body: [
      "The Snake Pit lets you record up to 30 seconds of vocals and send them to our A&R desk. You must be 18 or older, or have a parent/guardian's consent, and you may only submit vocals and material you own or have the rights to.",
      "Submitting sends your recording to the label for review under the same terms as a talent demo. If the label features your submission as a Ghost Verse, you grant DGE: Cold-Blooded Records LLC™ a non-exclusive, royalty-free license to stream that audition publicly on this website while it is featured. You keep ownership of your performance.",
      "No unlawful, infringing, hateful, or harassing content. The label may decline, unfeature, or remove any submission at its discretion. Being featured as a Ghost Verse is not a contract offer and creates no obligation on either side.",
    ],
  },
  {
    id: "conduct",
    icon: Scale,
    title: "5. Fair Use & Conduct",
    body: [
      "The Medusa Room's beats, mixes, stems, and visuals are the label's property. Listening, playing, and recording your own vocals over the room's beat inside the Snake Pit is encouraged; ripping, re-hosting, or redistributing the room's audio or visuals is not.",
      "Do not disrupt the room for others: no leaderboard spam, offensive player names, or attempts to bypass gates, timers, or the crystal puzzle. Offending names and scores are removed.",
    ],
  },
  {
    id: "changes",
    icon: RefreshCw,
    title: "6. Changes & General Terms",
    body: [
      "These Medusa Room terms supplement the main DGE Terms of Service & Policies (/terms), which govern licensing, purchases, and general site use. Where they conflict, the main Terms control for purchases and these rules control for room features.",
      "We may update these rules as the room evolves; the current version is always posted here. Continued use of the Medusa Room after an update means you accept the new rules.",
    ],
  },
];

export default function MedusaTermsPage() {
  const contact = useContact();
  useEffect(() => {
    document.title = "Medusa Room Terms & Rules | DGE: Cold-Blooded Records LLC™";
    return () => { document.title = "DGE: Cold-Blooded Records LLC™ | Official ASCAP® Member Label — Est. 2026"; };
  }, []);

  return (
    <main className="bg-obsidian text-ice min-h-screen" data-testid="medusa-terms-page">
      <Navbar />
      <div className="pt-[70px]">
        <header className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-12 text-center">
          <img src="/medusa-logo.webp" alt="" className="w-28 sm:w-36 mx-auto drop-shadow-[0_0_30px_rgba(168,85,247,0.45)]" />
          <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.3em] text-fuchsia-400">The Medusa Room</p>
          <h1 className="mt-2 font-display text-3xl sm:text-5xl font-bold text-white" data-testid="medusa-terms-title">Terms, Policies & House Rules</h1>
          <p className="mt-4 text-sm text-ice/60 max-w-xl mx-auto leading-relaxed">
            The rules of the room — membership and billing, the VIP Vault, arcade prizes, and Snake Pit submissions. Effective October 2026.
          </p>
        </header>

        <div className="max-w-4xl mx-auto px-5 sm:px-8 pb-16 space-y-4" data-testid="medusa-terms-sections">
          {SECTIONS.map(({ id, icon: Icon, title, body }) => (
            <section key={id} className="rounded-2xl border border-[#26262e] bg-[#0B0B10]/80 p-6" data-testid={`medusa-terms-${id}`}>
              <div className="flex items-center gap-2.5 mb-3">
                <Icon size={17} className="text-fuchsia-400 shrink-0" />
                <h2 className="font-display text-white font-bold text-base sm:text-lg">{title}</h2>
              </div>
              <div className="space-y-3">
                {body.map((p, i) => (
                  <p key={i} className="text-sm text-ice/60 leading-relaxed">{p}</p>
                ))}
              </div>
            </section>
          ))}

          <section className="rounded-2xl border border-teal-500/25 bg-teal-950/10 p-6" data-testid="medusa-terms-contact">
            <div className="flex items-center gap-2.5 mb-3">
              <Mail size={17} className="text-teal-300 shrink-0" />
              <h2 className="font-display text-white font-bold text-base sm:text-lg">7. Questions</h2>
            </div>
            <p className="text-sm text-ice/60 leading-relaxed">
              Membership or billing questions: <a href={`mailto:${contact.support}`} className="text-teal-300 hover:text-teal-200 transition-colors">{contact.support}</a>.
              Everything else about the room: <a href={`mailto:${contact.email}`} className="text-teal-300 hover:text-teal-200 transition-colors">{contact.email}</a>.
              See also the <Link to="/medusa-guide" className="text-teal-300 hover:text-teal-200 transition-colors">Visitor's Guide</Link> and the main{" "}
              <Link to="/terms" className="text-teal-300 hover:text-teal-200 transition-colors">Terms of Service</Link>.
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </main>
  );
}
